// LayoutExternal.jsx (UI-only update: align desktop nav pills with logo + username, add mobile drawer)
// Functionality unchanged.
import React, { useEffect, useMemo, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import logo from "../assets/logo.png";
import { LayoutDashboard, Map, Globe, Users, Circle } from "lucide-react";
import { BRANDS, getSelectedBrandCode, setSelectedBrandCode } from "../utils/brandContext";

const LayoutExternal = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const [brandCode, setBrandCode] = useState(getSelectedBrandCode());

    const [auth, setAuth] = useState(() => ({
        username: localStorage.getItem("username"),
        email: localStorage.getItem("email"),
    }));

    const syncAuthFromStorage = () => {
        setAuth({
            username: localStorage.getItem("username"),
            email: localStorage.getItem("email"),
        });
    };

    useEffect(() => {
        syncAuthFromStorage();
        window.addEventListener("authUpdated", syncAuthFromStorage);
        window.addEventListener("storage", syncAuthFromStorage);
        return () => {
            window.removeEventListener("authUpdated", syncAuthFromStorage);
            window.removeEventListener("storage", syncAuthFromStorage);
        };
    }, []);

    useEffect(() => setOpen(false), [location.pathname]);
    useEffect(() => {
        const syncBrand = () => setBrandCode(getSelectedBrandCode());
        window.addEventListener("brandUpdated", syncBrand);
        window.addEventListener("storage", syncBrand);
        return () => {
            window.removeEventListener("brandUpdated", syncBrand);
            window.removeEventListener("storage", syncBrand);
        };
    }, []);

    const onBrandChange = (e) => {
        const code = e.target.value;
        if (code === brandCode) return;
        setSelectedBrandCode(code);
        setBrandCode(code);
        window.location.reload();
    };

    const signOut = () => {
        localStorage.removeItem("externalAccessToken");
        localStorage.removeItem("username");
        localStorage.removeItem("email");
        navigate("/external");
    };

    const menuItems = useMemo(
        () => [
            { path: "/dashboard-external", name: "Dashboard" },
            { path: "/map-view-external", name: "Map" },
            { path: "/domain-management-external", name: "Domains" },
            { path: "/contact-management-external", name: "Contacts" },
        ],
        []
    );

    const iconMap = {
        "/dashboard-external": LayoutDashboard,
        "/map-view-external": Map,
        "/domain-management-external": Globe,
        "/contact-management-external": Users,
    };

    const DesktopNavLinks = () => (
        <nav className="hidden md:flex items-center">
            <ul className="flex items-center gap-2">
                {menuItems.map(({ path, name }, i) => {
                    const active = location.pathname === path;
                    const Icon = iconMap[path] || Circle;

                    return (
                        <li key={`${path}-${i}`}>
                            <Link
                                to={path}
                                className={[
                                    "flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors duration-150 border",
                                    active
                                        ? "bg-slate-900 text-white border-slate-900 shadow-sm"
                                        : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100 hover:text-slate-900",
                                ].join(" ")}
                            >
                                <Icon
                                    className={`h-4 w-4 shrink-0 ${active ? "text-white" : "text-slate-400"
                                        }`}
                                    aria-hidden="true"
                                />
                                <span className="truncate">{name}</span>
                            </Link>
                        </li>
                    );
                })}
            </ul>
        </nav>
    );

    const DrawerNavLinks = ({ onNavigate }) => (
        <nav className="w-full">
            <ul className="flex flex-col gap-2 px-3 md:px-4 py-3">
                {menuItems.map(({ path, name }, i) => {
                    const active = location.pathname === path;
                    const Icon = iconMap[path] || Circle;

                    return (
                        <li key={`${path}-${i}`} className="w-full">
                            <Link
                                to={path}
                                onClick={onNavigate}
                                className={[
                                    "flex w-full items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors duration-150 border",
                                    active
                                        ? "bg-slate-900 text-white border-slate-900 shadow-sm"
                                        : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100 hover:text-slate-900",
                                ].join(" ")}
                            >
                                <Icon
                                    className={`h-4 w-4 shrink-0 ${active ? "text-white" : "text-slate-400"
                                        }`}
                                    aria-hidden="true"
                                />
                                <span className="truncate">{name}</span>
                            </Link>
                        </li>
                    );
                })}
            </ul>
        </nav>
    );

    return (
        <div className="min-h-screen w-full bg-gray-100 flex flex-col">
            {/* Skip link for accessibility */}
            <a
                href="#main"
                className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:rounded-xl focus:bg-white focus:px-3 focus:py-2 focus:text-slate-900 focus:ring-2 focus:ring-slate-400/60"
            >
                Skip to content
            </a>

            {/* Top bar (all sizes) */}
            <header className="sticky top-0 z-40 w-full border-b border-slate-200 bg-gray-100">
                {/* Single row: Brand + (desktop nav) + user + hamburger (mobile) */}
                <div className="h-16 flex items-center justify-between px-4 gap-4">
                    <div className="flex items-center gap-3 shrink-0">
                        <img src={logo} alt="Logo" className="h-7 w-7" />
                        <span className="text-lg font-extrabold">DataEnhancer</span>
                    </div>

                    {/* Desktop nav sits in the middle and can scroll if needed */}
                    <div className="hidden md:flex flex-1 min-w-0 justify-center">
                        <div className="max-w-full overflow-x-auto no-scrollbar">
                            <DesktopNavLinks />
                        </div>
                    </div>

                    <div className="flex items-center gap-3 shrink-0">
                        <div className="hidden md:flex items-center">
                            <select
                                id="brand-select-desktop-external"
                                value={brandCode}
                                onChange={onBrandChange}
                                className="rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400/60"
                            >
                                {BRANDS.map((b) => (
                                    <option key={b.code} value={b.code}>
                                        {b.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <span className="hidden sm:inline text-sm font-medium text-slate-700">
                            {auth.username || "User"}
                        </span>

                        <button
                            onClick={signOut}
                            type="button"
                            className="hidden sm:inline-flex px-4 py-1 rounded-full bg-red-600 text-white shadow-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500/60 transition duration-200 ease-in-out"
                        >
                            Sign out
                        </button>

                        {/* Mobile hamburger */}
                        <button
                            aria-label="Open navigation"
                            aria-expanded={open}
                            aria-controls="mobile-drawer-external"
                            onClick={() => setOpen(true)}
                            className="md:hidden p-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-400/60"
                            type="button"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-7 w-7 text-slate-800"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                aria-hidden="true"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M4 6h16M4 12h16M4 18h16"
                                />
                            </svg>
                        </button>

                        {/* Compact sign out on very small screens */}
                        <button
                            onClick={signOut}
                            type="button"
                            className="sm:hidden inline-flex px-3 py-2 rounded-xl bg-red-600 text-white shadow-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500/60 transition"
                        >
                            Sign out
                        </button>
                    </div>
                </div>
            </header>

            {/* Mobile drawer + overlay */}
            <div
                id="mobile-drawer-external"
                className={`fixed inset-0 z-50 md:hidden ${open ? "" : "pointer-events-none"}`}
            >
                <div
                    className={`absolute inset-0 bg-black/40 transition-opacity ${open ? "opacity-100" : "opacity-0"
                        }`}
                    onClick={() => setOpen(false)}
                    aria-hidden="true"
                />

                <div
                    className={`absolute left-0 top-0 h-full w-[18rem] bg-gray-100 shadow-xl transition-transform duration-300 ${open ? "translate-x-0" : "-translate-x-full"
                        }`}
                    role="dialog"
                    aria-modal="true"
                >
                    <div className="relative h-full flex flex-col">
                        <button
                            aria-label="Close navigation"
                            onClick={() => setOpen(false)}
                            className="absolute right-3 top-3 p-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-400/60"
                            type="button"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-6 w-6 text-slate-700"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                                aria-hidden="true"
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </button>

                        <div className="flex items-center gap-3 p-4 pt-5 justify-center border-b border-slate-200">
                            <img src={logo} alt="Logo" className="h-8 w-8" />
                            <div className="text-xl font-extrabold">DataEnhancer</div>
                        </div>

                        <div className="px-4 pt-3">
                            <select
                                id="brand-select-mobile-external"
                                value={brandCode}
                                onChange={onBrandChange}
                                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400/60"
                            >
                                {BRANDS.map((b) => (
                                    <option key={b.code} value={b.code}>
                                        {b.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="flex-1 overflow-y-auto">
                            <DrawerNavLinks onNavigate={() => setOpen(false)} />
                        </div>

                        <div className="p-4 border-t border-slate-200">
                            <div className="flex flex-col items-center text-center">
                                <div className="mb-1 font-semibold text-md">
                                    {auth.username || "User"}
                                </div>
                                <div className="mb-3 text-sm text-slate-600 break-all">
                                    {auth.email || "Email"}
                                </div>

                                <button
                                    onClick={signOut}
                                    type="button"
                                    className="w-full px-4 py-2 rounded-full bg-red-600 text-white shadow-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500/60 transition"
                                >
                                    Sign out
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <main id="main" className="flex-1 w-full max-w-full overflow-y-auto">
                <div className="mx-auto w-full max-w-full px-3 sm:px-4 md:px-6 pb-6 pt-4 sm:pt-5 md:pt-6">
                    <Outlet />
                </div>
            </main>

            {/* Footer */}
            <footer className="w-full border-t border-slate-200 bg-gray-100">
                <div className="mx-auto w-full max-w-full px-4 py-3 text-xs text-slate-500 flex items-center justify-between">
                    <span>{auth.email || ""}</span>
                    <span className="hidden sm:inline">© CiiLOCK Engineering</span>
                </div>
            </footer>
        </div>
    );
};

export default LayoutExternal;
